import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Input,
  Button,
  Select,
  Modal,
  Form,
  Tag,
  Space,
  Row,
  Col,
  Typography,
  Tooltip,
  message,
  Empty,
  Descriptions,
  Divider,
  Alert,
  Statistic,
  Pagination,
  DatePicker,
  Tabs,
  Progress,
  Badge,
  Radio,
  Spin,
  Popconfirm
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  FileSearchOutlined,
  LineChartOutlined,
  CommentOutlined,
  LikeOutlined,
  DislikeOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';
import './AIJudgeManagement.css';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const AIJudgeManagement = () => {
  // 标签页状态
  const [activeTab, setActiveTab] = useState('records');

  // AI审核记录相关状态
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // 筛选条件
  const [filters, setFilters] = useState({
    requestId: '',
    applicantId: '',
    suggestedAction: '',
    startDate: null,
    endDate: null,
    minScore: null,
    maxScore: null
  });

  // 统计数据相关状态
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);

  // 反馈统计相关状态
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [feedbackStatsLoading, setFeedbackStatsLoading] = useState(false);
  const [feedbackDateRange, setFeedbackDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);

  // 详情模态框
  const [detailModal, setDetailModal] = useState({
    visible: false,
    record: null,
    loading: false
  });

  // 反馈模态框
  const [feedbackModal, setFeedbackModal] = useState({
    visible: false,
    recordId: null,
    loading: false
  });

  const [feedbackForm] = Form.useForm();

  // 批量删除相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // 按条件删除模态框
  const [deleteByConditionModal, setDeleteByConditionModal] = useState({
    visible: false,
    loading: false
  });
  const [deleteConditionForm] = Form.useForm();

  // 获取AI审核记录列表
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: limit
      };

      // 添加筛选条件
      if (filters.requestId) params.requestId = filters.requestId;
      if (filters.applicantId) params.applicantId = filters.applicantId;
      if (filters.suggestedAction) params.suggestedAction = filters.suggestedAction;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.minScore !== null && filters.minScore !== '') params.minScore = filters.minScore;
      if (filters.maxScore !== null && filters.maxScore !== '') params.maxScore = filters.maxScore;

      const response = await API.get('/api/admin/ai-judge-records', { params });
      
      if (response.data.code === 0) {
        setRecords(response.data.data.records);
        setTotalRecords(response.data.data.pagination.total);
      } else {
        message.error(response.data.msg || '获取AI审核记录失败');
      }
    } catch (error) {
      console.error('获取AI审核记录失败:', error);
      message.error('获取AI审核记录失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  // 获取统计数据
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (statsDateRange && statsDateRange[0] && statsDateRange[1]) {
        params.startDate = statsDateRange[0].format('YYYY-MM-DD');
        params.endDate = statsDateRange[1].format('YYYY-MM-DD');
      }

      const response = await API.get('/api/admin/ai-judge-statistics', { params });
      
      if (response.data.code === 0) {
        setStatistics(response.data.data);
      } else {
        message.error(response.data.msg || '获取统计数据失败');
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error('获取统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  }, [statsDateRange]);

  // 获取反馈统计
  const fetchFeedbackStats = useCallback(async () => {
    setFeedbackStatsLoading(true);
    try {
      const params = {};
      if (feedbackDateRange && feedbackDateRange[0] && feedbackDateRange[1]) {
        params.startDate = feedbackDateRange[0].format('YYYY-MM-DD');
        params.endDate = feedbackDateRange[1].format('YYYY-MM-DD');
      }

      const response = await API.get('/api/admin/ai-judge-feedback-stats', { params });
      
      if (response.data.code === 0) {
        setFeedbackStats(response.data.data);
      } else {
        message.error(response.data.msg || '获取反馈统计失败');
      }
    } catch (error) {
      console.error('获取反馈统计失败:', error);
      message.error('获取反馈统计失败');
    } finally {
      setFeedbackStatsLoading(false);
    }
  }, [feedbackDateRange]);

  // 获取单条记录详情
  const fetchRecordDetail = async (recordId) => {
    console.log('点击详情按钮，记录ID:', recordId);
    setDetailModal({ visible: true, record: null, loading: true });
    try {
      const response = await API.get(`/api/admin/ai-judge-records/${recordId}`);
      console.log('详情API响应:', response.data);
      
      if (response.data.code === 0) {
        setDetailModal({
          visible: true,
          record: response.data.data,
          loading: false
        });
      } else {
        message.error(response.data.msg || '获取记录详情失败');
        setDetailModal({ visible: false, record: null, loading: false });
      }
    } catch (error) {
      console.error('获取记录详情失败:', error);
      message.error('获取记录详情失败');
      setDetailModal({ visible: false, record: null, loading: false });
    }
  };

  // 提交反馈
  const submitFeedback = async (values) => {
    setFeedbackModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await API.post(
        `/api/admin/ai-judge-records/${feedbackModal.recordId}/feedback`,
        values
      );
      
      if (response.data.code === 0) {
        message.success('反馈提交成功');
        setFeedbackModal({ visible: false, recordId: null, loading: false });
        feedbackForm.resetFields();
        // 刷新列表
        fetchRecords();
        // 如果详情模态框打开，也刷新详情
        if (detailModal.visible && detailModal.record) {
          fetchRecordDetail(detailModal.record._id);
        }
      } else {
        message.error(response.data.msg || '反馈提交失败');
      }
    } catch (error) {
      console.error('反馈提交失败:', error);
      message.error('反馈提交失败');
    } finally {
      setFeedbackModal(prev => ({ ...prev, loading: false }));
    }
  };

  // 删除单条记录
  const handleDeleteRecord = async (recordId) => {
    try {
      const response = await API.delete(`/api/admin/ai-judge-records/${recordId}`);
      
      if (response.data.code === 0) {
        message.success('删除成功');
        fetchRecords();
      } else {
        message.error(response.data.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除记录失败:', error);
      message.error('删除失败');
    }
  };

  // 批量删除选中的记录
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的记录');
      return;
    }

    setBatchDeleteLoading(true);
    try {
      const response = await API.post('/api/admin/ai-judge-records/batch-delete', {
        recordIds: selectedRowKeys
      });
      
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setSelectedRowKeys([]);
        fetchRecords();
      } else {
        message.error(response.data.msg || '批量删除失败');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('批量删除失败');
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  // 按条件删除
  const handleDeleteByCondition = async (values) => {
    setDeleteByConditionModal(prev => ({ ...prev, loading: true }));
    try {
      const params = {};
      
      if (values.dateRange && values.dateRange.length === 2) {
        params.startDate = values.dateRange[0].format('YYYY-MM-DD');
        params.endDate = values.dateRange[1].format('YYYY-MM-DD');
      }
      
      if (values.suggestedAction) {
        params.suggestedAction = values.suggestedAction;
      }
      
      if (values.success !== undefined) {
        params.success = values.success;
      }

      const response = await API.post('/api/admin/ai-judge-records/batch-delete-by-condition', params);
      
      if (response.data.code === 0) {
        message.success(response.data.msg);
        setDeleteByConditionModal({ visible: false, loading: false });
        deleteConditionForm.resetFields();
        fetchRecords();
      } else {
        message.error(response.data.msg || '删除失败');
      }
    } catch (error) {
      console.error('按条件删除失败:', error);
      message.error('删除失败');
    } finally {
      setDeleteByConditionModal(prev => ({ ...prev, loading: false }));
    }
  };

  // 初始加载
  useEffect(() => {
    if (activeTab === 'records') {
      fetchRecords();
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    } else if (activeTab === 'feedback') {
      fetchFeedbackStats();
    }
  }, [activeTab, fetchRecords, fetchStatistics, fetchFeedbackStats]);

  // 处理筛选
  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // 重置筛选
  const resetFilters = () => {
    setFilters({
      requestId: '',
      applicantId: '',
      suggestedAction: '',
      startDate: null,
      endDate: null,
      minScore: null,
      maxScore: null
    });
    setCurrentPage(1);
  };

  // 获取建议动作的标签颜色和文本
  const getActionTag = (action) => {
    const actionMap = {
      auto_approve: { color: 'success', text: '自动通过', icon: <CheckCircleOutlined /> },
      manual_review: { color: 'warning', text: '人工审核', icon: <ExclamationCircleOutlined /> },
      auto_reject: { color: 'error', text: '自动拒绝', icon: <CloseCircleOutlined /> }
    };
    const config = actionMap[action] || { color: 'default', text: action, icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // 获取评分颜色
  const getScoreColor = (score) => {
    if (score >= 0.8) return '#52c41a';
    if (score >= 0.5) return '#faad14';
    return '#ff4d4f';
  };

  // 获取评分等级
  const getScoreLevel = (score) => {
    if (score >= 0.8) return '优秀';
    if (score >= 0.5) return '良好';
    return '较差';
  };

  // 表格列定义
  const columns = [
    {
      title: '申请人',
      dataIndex: ['applicantId', 'username'],
      key: 'applicant',
      width: 120,
      render: (username, record) => (
        <Space direction="vertical" size={0} style={{ display: 'flex' }}>
          <Text strong>{username}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.applicantId?.cloudPlatformUID}
          </Text>
        </Space>
      )
    },
    {
      title: 'IP信息',
      dataIndex: ['requestSnapshot', 'ipAddresses'],
      key: 'ipInfo',
      width: 150,
      render: (ipAddresses, record) => (
        <Space direction="vertical" size={0} style={{ display: 'flex' }}>
          <Tag color="blue">{record.requestSnapshot?.ipType?.toUpperCase()}</Tag>
          <Text style={{ fontSize: '12px' }}>
            {ipAddresses?.length || 0} 个IP
          </Text>
          {ipAddresses && ipAddresses.length > 0 && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {ipAddresses[0]}
              {ipAddresses.length > 1 && '...'}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'AI评分',
      dataIndex: ['aiResult', 'score'],
      key: 'score',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.aiResult?.score || 0) - (b.aiResult?.score || 0),
      render: (score) => (
        <Space direction="vertical" size={0} style={{ width: '100%', display: 'flex' }}>
          <Progress
            type="circle"
            percent={Math.round((score || 0) * 100)}
            size={50}
            strokeColor={getScoreColor(score)}
          />
          <Text style={{ fontSize: '12px', color: getScoreColor(score) }}>
            {getScoreLevel(score)}
          </Text>
        </Space>
      )
    },
    {
      title: '识别场景',
      dataIndex: ['aiResult', 'matched_scene'],
      key: 'scene',
      width: 120,
      render: (scene) => scene ? <Tag color="cyan">{scene}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: 'AI建议',
      dataIndex: 'suggestedAction',
      key: 'suggestedAction',
      width: 120,
      filters: [
        { text: '自动通过', value: 'auto_approve' },
        { text: '人工审核', value: 'manual_review' },
        { text: '自动拒绝', value: 'auto_reject' }
      ],
      render: (action) => getActionTag(action)
    },
    {
      title: '处理类型',
      dataIndex: 'processingType',
      key: 'processingType',
      width: 100,
      render: (type) => {
        const typeMap = {
          basic_rule: { color: 'default', text: '基础规则' },
          ai_judge: { color: 'purple', text: 'AI判断' },
          error: { color: 'error', text: '错误' }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '响应时间',
      dataIndex: 'responseTime',
      key: 'responseTime',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.responseTime || 0) - (b.responseTime || 0),
      render: (time) => (
        <Text style={{ fontSize: '12px' }}>
          {time ? `${time}ms` : '-'}
        </Text>
      )
    },
    {
      title: '状态',
      dataIndex: 'success',
      key: 'success',
      width: 80,
      align: 'center',
      render: (success) => (
        success ? 
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} /> :
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
      )
    },
    {
      title: '反馈',
      dataIndex: 'adminFeedback',
      key: 'feedback',
      width: 80,
      align: 'center',
      render: (feedback) => (
        feedback && feedback.agreed !== null ? (
          <Tooltip title={feedback.comment || '已反馈'}>
            {feedback.agreed ? 
              <LikeOutlined style={{ color: '#52c41a', fontSize: '18px' }} /> :
              <DislikeOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
            }
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        )
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => fetchRecordDetail(record._id)}
          >
            详情
          </Button>
          {(!record.adminFeedback || record.adminFeedback.agreed === null) && (
            <Button
              type="link"
              size="small"
              icon={<CommentOutlined />}
              onClick={() => {
                setFeedbackModal({ visible: true, recordId: record._id, loading: false });
              }}
            >
              反馈
            </Button>
          )}
          <Popconfirm
            title="确定要删除这条记录吗？"
            onConfirm={() => handleDeleteRecord(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 渲染筛选区域
  const renderFilters = () => (
    <Card style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Input
            placeholder="申请ID"
            value={filters.requestId}
            onChange={(e) => handleFilter('requestId', e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="申请人ID"
            value={filters.applicantId}
            onChange={(e) => handleFilter('applicantId', e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder="AI建议动作"
            value={filters.suggestedAction || undefined}
            onChange={(value) => handleFilter('suggestedAction', value)}
            style={{ width: '100%' }}
            allowClear
          >
            <Option value="auto_approve">自动通过</Option>
            <Option value="manual_review">人工审核</Option>
            <Option value="auto_reject">自动拒绝</Option>
          </Select>
        </Col>
        <Col span={6}>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchRecords()}
            >
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={resetFilters}
            >
              重置
            </Button>
          </Space>
        </Col>
        <Col span={6}>
          <Input
            placeholder="最低评分 (0-1)"
            type="number"
            min={0}
            max={1}
            step={0.1}
            value={filters.minScore}
            onChange={(e) => handleFilter('minScore', e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="最高评分 (0-1)"
            type="number"
            min={0}
            max={1}
            step={0.1}
            value={filters.maxScore}
            onChange={(e) => handleFilter('maxScore', e.target.value)}
            allowClear
          />
        </Col>
        <Col span={12}>
          <RangePicker
            style={{ width: '100%' }}
            value={filters.startDate && filters.endDate ? [dayjs(filters.startDate), dayjs(filters.endDate)] : null}
            onChange={(dates) => {
              if (dates) {
                handleFilter('startDate', dates[0].format('YYYY-MM-DD'));
                handleFilter('endDate', dates[1].format('YYYY-MM-DD'));
              } else {
                handleFilter('startDate', null);
                handleFilter('endDate', null);
              }
            }}
            placeholder={['开始日期', '结束日期']}
          />
        </Col>
      </Row>
    </Card>
  );

  // 渲染记录列表标签页
  const renderRecordsTab = () => (
    <>
      {renderFilters()}
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>AI审核记录</span>
            <Badge count={totalRecords} showZero style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchRecords}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {selectedRowKeys.length > 0 && (
          <Alert
            message={
              <Space>
                <span>已选择 {selectedRowKeys.length} 条记录</span>
                <Popconfirm
                  title={`确定要删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                  onConfirm={handleBatchDelete}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button
                    type="primary"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={batchDeleteLoading}
                  >
                    批量删除
                  </Button>
                </Popconfirm>
                <Button
                  size="small"
                  onClick={() => setSelectedRowKeys([])}
                >
                  取消选择
                </Button>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ marginBottom: 16 }}>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => setDeleteByConditionModal({ visible: true, loading: false })}
          >
            按条件删除
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={records}
          rowKey="_id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1500 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            preserveSelectedRowKeys: true
          }}
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={currentPage}
            total={totalRecords}
            pageSize={limit}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
            showTotal={(total) => `共 ${total} 条记录`}
          />
        </div>
      </Card>
    </>
  );

  // 渲染统计标签页
  const renderStatisticsTab = () => (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Text>日期范围：</Text>
          <RangePicker
            value={statsDateRange}
            onChange={(dates) => setStatsDateRange(dates)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchStatistics}
            loading={statsLoading}
          >
            查询
          </Button>
        </Space>
      </Card>

      {statsLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : statistics ? (
        <>
          {/* 总览统计 */}
          <Card title="总览统计" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总审核次数"
                  value={statistics.summary?.totalCount || 0}
                  prefix={<RobotOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="成功次数"
                  value={statistics.summary?.successCount || 0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="错误次数"
                  value={statistics.summary?.errorCount || 0}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均评分"
                  value={statistics.summary?.avgScore?.toFixed(2) || 0}
                  suffix="/ 1.0"
                  valueStyle={{ color: getScoreColor(statistics.summary?.avgScore) }}
                />
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="自动通过"
                  value={statistics.summary?.autoApproveCount || 0}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="人工审核"
                  value={statistics.summary?.manualReviewCount || 0}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="自动拒绝"
                  value={statistics.summary?.autoRejectCount || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均响应时间"
                  value={statistics.summary?.avgResponseTime || 0}
                  suffix="ms"
                  prefix={<ThunderboltOutlined />}
                />
              </Col>
            </Row>
          </Card>

          {/* 场景分布 */}
          {statistics.sceneDistribution && statistics.sceneDistribution.length > 0 && (
            <Card title="场景分布" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                {statistics.sceneDistribution.map((scene, index) => (
                  <Col span={8} key={index}>
                    <Card size="small">
                      <Statistic
                        title={scene._id || '未识别'}
                        value={scene.count}
                        suffix={`次 (平均分: ${scene.avgScore?.toFixed(2)})`}
                        valueStyle={{ fontSize: '18px' }}
                      />
                      <Progress
                        percent={Math.round((scene.avgScore || 0) * 100)}
                        strokeColor={getScoreColor(scene.avgScore)}
                        size="small"
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}

          {/* 评分分布 */}
          {statistics.scoreDistribution && statistics.scoreDistribution.length > 0 && (
            <Card title="评分分布" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                {statistics.scoreDistribution.map((score, index) => {
                  const rangeStart = score._id;
                  const rangeEnd = index < statistics.scoreDistribution.length - 1 
                    ? statistics.scoreDistribution[index + 1]._id 
                    : 1.0;
                  return (
                    <Col span={6} key={index}>
                      <Card size="small">
                        <Statistic
                          title={`${rangeStart.toFixed(1)} - ${rangeEnd.toFixed(1)}`}
                          value={score.count}
                          suffix={`次 (平均: ${score.avgScore?.toFixed(2)})`}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          )}

          {/* 每日趋势 */}
          {statistics.dailyTrend && statistics.dailyTrend.length > 0 && (
            <Card title="每日趋势">
              <Table
                dataSource={statistics.dailyTrend}
                rowKey="_id"
                pagination={false}
                scroll={{ x: 800 }}
                columns={[
                  {
                    title: '日期',
                    dataIndex: '_id',
                    key: 'date',
                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                  },
                  {
                    title: '总数',
                    dataIndex: 'count',
                    key: 'count',
                    align: 'center'
                  },
                  {
                    title: '成功',
                    dataIndex: 'successCount',
                    key: 'successCount',
                    align: 'center'
                  },
                  {
                    title: '平均分',
                    dataIndex: 'avgScore',
                    key: 'avgScore',
                    align: 'center',
                    render: (score) => (
                      <Tag color={getScoreColor(score)}>
                        {score?.toFixed(2)}
                      </Tag>
                    )
                  },
                  {
                    title: '自动通过',
                    dataIndex: 'autoApproveCount',
                    key: 'autoApproveCount',
                    align: 'center'
                  },
                  {
                    title: '人工审核',
                    dataIndex: 'manualReviewCount',
                    key: 'manualReviewCount',
                    align: 'center'
                  },
                  {
                    title: '自动拒绝',
                    dataIndex: 'autoRejectCount',
                    key: 'autoRejectCount',
                    align: 'center'
                  }
                ]}
              />
            </Card>
          )}
        </>
      ) : (
        <Card>
          <Empty description="暂无统计数据" />
        </Card>
      )}
    </>
  );

  // 渲染反馈统计标签页
  const renderFeedbackTab = () => (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <Text>日期范围：</Text>
          <RangePicker
            value={feedbackDateRange}
            onChange={(dates) => setFeedbackDateRange(dates)}
            placeholder={['开始日期', '结束日期']}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchFeedbackStats}
            loading={feedbackStatsLoading}
          >
            查询
          </Button>
        </Space>
      </Card>

      {feedbackStatsLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : feedbackStats ? (
        <>
          {/* 总体准确率 */}
          <Card title="总体准确率" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总反馈数"
                  value={feedbackStats.summary?.totalFeedback || 0}
                  prefix={<CommentOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="同意数"
                  value={feedbackStats.summary?.agreedCount || 0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<LikeOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="不同意数"
                  value={feedbackStats.summary?.disagreedCount || 0}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<DislikeOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总体准确率"
                  value={feedbackStats.summary?.overallAccuracy 
                    ? (parseFloat(feedbackStats.summary.overallAccuracy) * 100).toFixed(2)
                    : 0
                  }
                  suffix="%"
                  valueStyle={{ 
                    color: parseFloat(feedbackStats.summary?.overallAccuracy || 0) >= 0.8 
                      ? '#3f8600' 
                      : '#faad14' 
                  }}
                />
              </Col>
            </Row>
            <Divider />
            <Progress
              percent={feedbackStats.summary?.overallAccuracy 
                ? parseFloat(feedbackStats.summary.overallAccuracy) * 100
                : 0
              }
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              status="active"
            />
          </Card>

          {/* 各动作准确率 */}
          {feedbackStats.accuracyByAction && feedbackStats.accuracyByAction.length > 0 && (
            <Card title="各动作准确率">
              <Row gutter={16}>
                {feedbackStats.accuracyByAction.map((item, index) => {
                  const actionNames = {
                    auto_approve: '自动通过',
                    manual_review: '人工审核',
                    auto_reject: '自动拒绝'
                  };
                  return (
                    <Col span={8} key={index}>
                      <Card size="small">
                        <Statistic
                          title={actionNames[item.action] || item.action}
                          value={(item.accuracy * 100).toFixed(2)}
                          suffix="%"
                          valueStyle={{ 
                            color: item.accuracy >= 0.8 ? '#3f8600' : '#faad14'
                          }}
                        />
                        <div style={{ marginTop: 16 }}>
                          <Text type="secondary">
                            同意: {item.agreed} / 总数: {item.total}
                          </Text>
                        </div>
                        <Progress
                          percent={item.accuracy * 100}
                          strokeColor={item.accuracy >= 0.8 ? '#52c41a' : '#faad14'}
                          size="small"
                          style={{ marginTop: 8 }}
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <Empty description="暂无反馈统计数据" />
        </Card>
      )}
    </>
  );

  // 渲染详情模态框
  const renderDetailModal = () => {
    const record = detailModal.record;

    return (
      <Modal
        title={
          <Space>
            <FileSearchOutlined />
            <span>AI审核记录详情</span>
          </Space>
        }
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, record: null, loading: false })}
        footer={
          record ? [
            <Button key="close" onClick={() => setDetailModal({ visible: false, record: null, loading: false })}>
              关闭
            </Button>,
            (!record.adminFeedback || record.adminFeedback.agreed === null) && (
              <Button
                key="feedback"
                type="primary"
                icon={<CommentOutlined />}
                onClick={() => {
                  setFeedbackModal({ visible: true, recordId: record._id, loading: false });
                }}
              >
                提供反馈
              </Button>
            )
          ] : [
            <Button key="close" onClick={() => setDetailModal({ visible: false, record: null, loading: false })}>
              关闭
            </Button>
          ]
        }
        width={900}
      >
        <Spin spinning={detailModal.loading}>
          {!record ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="secondary">加载中...</Text>
            </div>
          ) : (
            <>
          {/* 基本信息 */}
          <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="记录ID">{record._id}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="处理类型">
                {record.processingType === 'basic_rule' && <Tag color="default">基础规则</Tag>}
                {record.processingType === 'ai_judge' && <Tag color="purple">AI判断</Tag>}
                {record.processingType === 'error' && <Tag color="error">错误</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                {record.success ? 
                  <Tag color="success" icon={<CheckCircleOutlined />}>成功</Tag> :
                  <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>
                }
              </Descriptions.Item>
              <Descriptions.Item label="使用模型">{record.model || '-'}</Descriptions.Item>
              <Descriptions.Item label="响应时间">{record.responseTime}ms</Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 申请人信息 */}
          {record.applicantId && (
            <Card title="申请人信息" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="用户名">{record.applicantId.username}</Descriptions.Item>
                <Descriptions.Item label="邮箱">{record.applicantId.email}</Descriptions.Item>
                <Descriptions.Item label="云平台UID">{record.applicantId.cloudPlatformUID}</Descriptions.Item>
                <Descriptions.Item label="角色">{record.applicantId.role}</Descriptions.Item>
                <Descriptions.Item label="账号类型">{record.applicantId.accountType}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* 申请信息快照 */}
          {record.requestSnapshot && (
            <Card title="申请信息" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="IP类型">
                  <Tag color="blue">{record.requestSnapshot.ipType?.toUpperCase()}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="IP数量">{record.requestSnapshot.ipCount}</Descriptions.Item>
                <Descriptions.Item label="IP地址">
                  {record.requestSnapshot.ipAddresses?.map((ip, idx) => (
                    <Tag key={idx}>{ip}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="申请理由">
                  <Paragraph style={{ marginBottom: 0 }}>
                    {record.requestSnapshot.applicationReason}
                  </Paragraph>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* AI判断结果 */}
          {record.aiResult && (
            <Card title="AI判断结果" size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="是否合理">
                  {record.aiResult.reasonable ? 
                    <Tag color="success" icon={<CheckCircleOutlined />}>合理</Tag> :
                    <Tag color="error" icon={<CloseCircleOutlined />}>不合理</Tag>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="评分">
                  <Space>
                    <Progress
                      type="circle"
                      percent={Math.round((record.aiResult.score || 0) * 100)}
                      size={60}
                      strokeColor={getScoreColor(record.aiResult.score)}
                    />
                    <Text style={{ color: getScoreColor(record.aiResult.score) }}>
                      {getScoreLevel(record.aiResult.score)}
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="识别场景" span={2}>
                  {record.aiResult.matched_scene ? 
                    <Tag color="cyan">{record.aiResult.matched_scene}</Tag> :
                    <Text type="secondary">-</Text>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="问题列表" span={2}>
                  {record.aiResult.problems && record.aiResult.problems.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {record.aiResult.problems.map((problem, idx) => (
                        <Tag key={idx} color="warning">{problem}</Tag>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">无</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="总结" span={2}>
                  <Paragraph style={{ marginBottom: 0, wordBreak: 'break-word' }}>
                    {record.aiResult.summary}
                  </Paragraph>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* AI建议 */}
          <Card title="AI建议" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="建议动作">
                {getActionTag(record.suggestedAction)}
              </Descriptions.Item>
              <Descriptions.Item label="建议原因">
                <Paragraph style={{ marginBottom: 0 }}>
                  {record.suggestedReason}
                </Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="实际动作">
                {getActionTag(record.actualAction)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 管理员反馈 */}
          {record.adminFeedback && record.adminFeedback.agreed !== null && (
            <Card title="管理员反馈" size="small">
              <Alert
                message={
                  <Space>
                    {record.adminFeedback.agreed ? 
                      <LikeOutlined style={{ color: '#52c41a' }} /> :
                      <DislikeOutlined style={{ color: '#ff4d4f' }} />
                    }
                    <Text strong>
                      {record.adminFeedback.agreed ? '同意AI判断' : '不同意AI判断'}
                    </Text>
                  </Space>
                }
                description={
                  <div>
                    <Paragraph style={{ marginTop: 8 }}>
                      {record.adminFeedback.comment || '无评论'}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      反馈时间: {dayjs(record.adminFeedback.feedbackAt).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                    {record.adminFeedback.feedbackBy && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          反馈人: {record.adminFeedback.feedbackBy.username}
                        </Text>
                      </>
                    )}
                  </div>
                }
                type={record.adminFeedback.agreed ? 'success' : 'warning'}
              />
            </Card>
          )}

          {/* 错误信息 */}
          {record.error && (
            <Card title="错误信息" size="small" style={{ marginTop: 16 }}>
              <Alert message={record.error} type="error" />
            </Card>
          )}
          </>
          )}
        </Spin>
      </Modal>
    );
  };

  // 渲染反馈模态框
  const renderFeedbackModal = () => (
    <Modal
      title={
        <Space>
          <CommentOutlined />
          <span>提供反馈</span>
        </Space>
      }
      open={feedbackModal.visible}
      onCancel={() => {
        setFeedbackModal({ visible: false, recordId: null, loading: false });
        feedbackForm.resetFields();
      }}
      onOk={() => feedbackForm.submit()}
      confirmLoading={feedbackModal.loading}
      okText="提交"
      cancelText="取消"
    >
      <Form
        form={feedbackForm}
        layout="vertical"
        onFinish={submitFeedback}
      >
        <Form.Item
          name="agreed"
          label="是否同意AI的判断"
          rules={[{ required: true, message: '请选择是否同意' }]}
        >
          <Radio.Group>
            <Radio value={true}>
              <Space>
                <LikeOutlined style={{ color: '#52c41a' }} />
                同意
              </Space>
            </Radio>
            <Radio value={false}>
              <Space>
                <DislikeOutlined style={{ color: '#ff4d4f' }} />
                不同意
              </Space>
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="comment"
          label="反馈意见"
        >
          <TextArea
            rows={4}
            placeholder="请输入您的反馈意见（可选）"
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  // 渲染按条件删除模态框
  const renderDeleteByConditionModal = () => (
    <Modal
      title={
        <Space>
          <DeleteOutlined />
          <span>按条件删除记录</span>
        </Space>
      }
      open={deleteByConditionModal.visible}
      onCancel={() => {
        setDeleteByConditionModal({ visible: false, loading: false });
        deleteConditionForm.resetFields();
      }}
      onOk={() => deleteConditionForm.submit()}
      confirmLoading={deleteByConditionModal.loading}
      okText="删除"
      cancelText="取消"
      okButtonProps={{ danger: true }}
    >
      <Alert
        message="警告"
        description="按条件删除将永久删除符合条件的记录，且无法恢复。单次最多删除1000条记录。"
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form
        form={deleteConditionForm}
        layout="vertical"
        onFinish={handleDeleteByCondition}
      >
        <Form.Item
          name="dateRange"
          label="日期范围"
        >
          <RangePicker
            style={{ width: '100%' }}
            placeholder={['开始日期', '结束日期']}
          />
        </Form.Item>
        <Form.Item
          name="suggestedAction"
          label="AI建议动作"
        >
          <Select placeholder="请选择" allowClear>
            <Option value="auto_approve">自动通过</Option>
            <Option value="manual_review">人工审核</Option>
            <Option value="auto_reject">自动拒绝</Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="success"
          label="执行状态"
        >
          <Select placeholder="请选择" allowClear>
            <Option value={true}>成功</Option>
            <Option value={false}>失败</Option>
          </Select>
        </Form.Item>
        <Alert
          message="提示"
          description="请至少选择一个删除条件。如果不选择任何条件，将无法执行删除操作。"
          type="info"
          showIcon
        />
      </Form>
    </Modal>
  );

  return (
    <div className="ai-judge-management">
      <div className="page-header">
        <Title level={2}>
          <RobotOutlined /> AI审核管理
        </Title>
        <Paragraph type="secondary">
          查看和管理IP权限申请的AI审核记录、统计数据和反馈信息
        </Paragraph>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'records',
            label: (
              <span>
                <FileSearchOutlined />
                审核记录
              </span>
            ),
            children: renderRecordsTab()
          },
          {
            key: 'statistics',
            label: (
              <span>
                <BarChartOutlined />
                统计分析
              </span>
            ),
            children: renderStatisticsTab()
          },
          {
            key: 'feedback',
            label: (
              <span>
                <LineChartOutlined />
                反馈统计
              </span>
            ),
            children: renderFeedbackTab()
          }
        ]}
      />

      {renderDetailModal()}
      {renderFeedbackModal()}
      {renderDeleteByConditionModal()}
    </div>
  );
};

export default AIJudgeManagement;

